import dayjs from 'dayjs';

import {
  PARTNERS,
  PARTNER_CHANNEL,
  PARTNER_TYPES,
  PARTNER_TYPE_CHANNEL,
} from './constants';
import {
  EcommerceCustomer,
  EcommerceProduct,
  EcommerceVehicle,
  ProductCheckoutDataLayer,
  ProductDataLayerBase,
  ProductOrderPurchaseDataLayer,
} from './models';
import ROUTES, { pathWithChannel } from 'libs/constants/routes';
import { getChannelName, isPilotChannelLanding } from 'libs/utils/routes';
import { capitalizeFirstLetter } from 'libs/utils/string';

const urlsExclude = [
  ROUTES.LANDING_AE,
  ROUTES.QUOTATION_AE,
  ROUTES.PAYMENT_AE,
  ROUTES.THANKS_AE,
  ROUTES.CONFIRMATION_AE,
  ROUTES.LANDING_MIRROR_AE,
  ROUTES.QUOTATION_MIRROR_AE,
  ROUTES.PAYMENT_MIRROR_AE,
  pathWithChannel('principal', 'bcpautoefectivo'),
];

export class DataLayer {
  static dataLayer = window.dataLayer;

  static pathname = document.location.pathname;

  static channelName = (sessionStorage.getItem('channel') ?? '').toUpperCase();
  static channelNameFormated = capitalizeFirstLetter(this.channelName);

  static isAutosProduct = !urlsExclude.includes(this.pathname);
  static isAutoefectivo = window.history.state?.usr?.autoefectivo;
  static enabledTaggeo = this.isAutosProduct && !this.isAutoefectivo;

  static setVirtualPage({
    pagePath,
    pageName,
    flujo,
    classPage,
    extra = {},
    channel = '',
  }: {
    pagePath: string;
    pageName: string;
    flujo: string;
    classPage: string;
    extra?: Record<string, string>;
    channel?: string;
  }): void {
    this.enabledTaggeo &&
      this.dataLayer.push({
        event: 'virtualPage',
        pageName: `Seguro Vehicular - ${pageName}`,
        pagePath: `/seguro-vehicular/${pagePath}`,
        clasePagina: classPage,
        flujo,
        partner: isPilotChannelLanding(this.pathname)
          ? PARTNER_CHANNEL[getChannelName()]
          : this.channelName ||
            (PARTNER_CHANNEL[this.pathname.toLowerCase()] ?? PARTNERS.pacifico),
        partnerType: isPilotChannelLanding(this.pathname)
          ? PARTNER_TYPE_CHANNEL[getChannelName()]
          : this.channelName ||
            (PARTNER_TYPE_CHANNEL[this.pathname.toLowerCase()] ??
              PARTNER_TYPES.pacifico.default),
        ...extra,
      });
  }

  static setVirtualEvent({
    category,
    action,
    label,
    flujo,
    tipoZona,
    zona,
    subZona,
    sección,
    tipoEvento,
    tipoElemento,
    valor,
    extra = {},
    tipoPago,
  }: {
    category?: string;
    action?: string;
    label?: string;
    flujo?: string;
    tipoZona?: string;
    zona?: string;
    subZona?: string;
    sección?: string;
    tipoEvento?: string;
    tipoElemento?: string;
    valor?: string;
    extra?: { [key: string]: string };
    tipoPago?: any;
  }): void {
    this.enabledTaggeo &&
      this.dataLayer.push({
        event: 'virtualEvent',
        ...(category ? { category } : {}),
        ...(action ? { action } : {}),
        ...(label ? { label } : {}),
        flujo: flujo ?? '',
        partner: isPilotChannelLanding(this.pathname)
          ? PARTNER_CHANNEL[getChannelName()]
          : this.channelName ||
            (PARTNER_CHANNEL[this.pathname.toLowerCase()] ?? PARTNERS.pacifico),
        partnerType: isPilotChannelLanding(this.pathname)
          ? PARTNER_TYPE_CHANNEL[getChannelName()]
          : this.channelName ||
            (PARTNER_TYPE_CHANNEL[this.pathname.toLowerCase()] ??
              PARTNER_TYPES.pacifico.default),
        tipoZona,
        zona,
        subZona,
        sección,
        tipoEvento,
        tipoElemento,
        valor,
        ...extra,
        ...(tipoPago ? { tipoPago } : {}),
      });
  }

  static setProductImpression({
    quotationNumber,
    product,
    flujo,
    vehicle,
    customer,
    coupon,
  }: ProductDataLayerBase) {
    const base = DataLayer.getEcommerceDatalayerBase({
      event: 'productImpression',
      quotationNumber,
    });

    base.ecommerce.impressions = {
      ...DataLayer.getEcommerceProduct({
        product,
        flujo,
        vehicle,
        customer,
        coupon,
      }),
    };

    this.enabledTaggeo && this.dataLayer.push(base);
  }

  static setProductClick({
    quotationNumber,
    product,
    flujo,
    vehicle,
    customer,
    coupon,
  }: ProductDataLayerBase) {
    const base = DataLayer.getEcommerceDatalayerBase({
      event: 'productClick',
      quotationNumber,
    });

    base.ecommerce.click = {
      ...DataLayer.getEcommerceProduct({
        product,
        flujo,
        vehicle,
        customer,
        coupon,
      }),
    };

    this.enabledTaggeo && this.dataLayer.push(base);
  }

  static setProductAdd({
    quotationNumber,
    product,
    flujo,
    vehicle,
    customer,
    coupon,
  }: ProductDataLayerBase) {
    const base = DataLayer.getEcommerceDatalayerBase({
      event: 'addToCart',
      quotationNumber,
    });

    base.ecommerce.add = {
      ...DataLayer.getEcommerceProduct({
        product,
        flujo,
        vehicle,
        customer,
        coupon,
      }),
    };

    this.enabledTaggeo && this.dataLayer.push(base);
  }

  static setProductCheckout({
    quotationNumber,
    product,
    flujo,
    vehicle,
    customer,
    coupon,
    step,
  }: ProductCheckoutDataLayer) {
    const base = DataLayer.getEcommerceDatalayerBase({
      event: 'productCheckout',
      quotationNumber,
    });

    base.ecommerce.checkout = {
      actionField: { step },
      ...DataLayer.getEcommerceProduct({
        product,
        flujo,
        vehicle,
        customer,
        coupon,
      }),
    };

    this.enabledTaggeo && this.dataLayer.push(base);
  }

  static setProductOrderPurchase({
    quotationNumber,
    product,
    flujo,
    vehicle,
    customer,
    coupon,
    order,
  }: ProductOrderPurchaseDataLayer) {
    const base = DataLayer.getEcommerceDatalayerBase({
      event: 'orderPurchase',
      quotationNumber,
    });

    base.dimension20 =
      order.paymentMethod === 'card' ? 'Tarjeta' : 'Cargo en cuenta';
    base.ecommerce.purchase = {
      actionField: {
        id: order.policyNumber,
        affiliation: 'Ecommerce Vehicular',
        revenue: product.price,
        tax: '',
        shipping: '',
        coupon: coupon || '',
      },
      ...DataLayer.getEcommerceProduct({
        product,
        flujo,
        vehicle,
        customer,
        coupon,
      }),
    };

    this.enabledTaggeo && this.dataLayer.push(base);
  }

  private static getEcommerceDatalayerBase({
    quotationNumber,
    event,
  }: {
    quotationNumber: string;
    event: string;
  }): any {
    return {
      event,
      dimension12: quotationNumber,
      ecommerce: {
        currencyCode: 'USD',
      },
    };
  }

  private static getEcommerceProduct({
    product,
    vehicle,
    customer,
    flujo,
    coupon,
  }: {
    flujo: string;
    coupon?: string;
    product: EcommerceProduct;
    vehicle: EcommerceVehicle;
    customer: EcommerceCustomer;
  }) {
    return {
      products: [
        {
          id: product.id,
          name: product.name,
          price: product.price,
          brand: 'Pacífico',
          category: 'Seguro vehicular',
          variant: flujo,
          quantity: 1,
          coupon: coupon || '',
          dimension6: vehicle.brand,
          dimension7: vehicle.model,
          dimension8: vehicle.year,
          dimension10: customer.birthdate
            ? dayjs().diff(dayjs(customer.birthdate), 'years')
            : '',
          dimension14: customer.sex,
        },
      ],
    };
  }
}
